import React, { useState, useRef, useEffect } from 'react';

const quickReplies = [
  "Hello! How can I help you today?",
  "Could you please provide your requirements?",
  "We have received your details, our team will get back to you shortly.",
  "Thank you for reaching out to Verve Kitchens!",
  "Can we schedule a site visit?",
  "Please let us know your estimated budget."
];

const MessageInput = ({ onSend, disabled }) => {
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const fileInputRef = useRef(null);
  const quickRepliesRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((!content.trim() && !file) || disabled) return;
    onSend(content, file);
    setContent('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Close quick replies when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quickRepliesRef.current && !quickRepliesRef.current.contains(event.target)) {
        setShowQuickReplies(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col bg-transparent w-full">
      {file && (
        <div className="mb-2 p-3 bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-between relative mx-2">
          <div className="flex items-center gap-3">
            {file.type.startsWith('image/') ? (
              <img src={URL.createObjectURL(file)} alt="preview" className="w-12 h-12 object-cover rounded" />
            ) : (
              <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            <div className="flex flex-col max-w-[200px] sm:max-w-xs">
              <span className="text-sm font-medium text-gray-800 truncate">{file.name}</span>
              <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
            </div>
          </div>
          <button 
            type="button"
            onClick={clearFile}
            className="text-gray-400 hover:text-red-500 transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2 bg-transparent">
        {/* Quick Replies Button */}
        <div className="relative" ref={quickRepliesRef}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setShowQuickReplies(!showQuickReplies)}
            className={`w-10 h-10 mb-1 flex-shrink-0 rounded-full flex items-center justify-center transition-colors ${showQuickReplies ? 'bg-green-100 text-green-600' : 'text-gray-500 hover:bg-gray-200'} disabled:opacity-50`}
            title="Quick Replies"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </button>
          
          {showQuickReplies && (
            <div className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Quick Replies</span>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setContent(reply);
                      setShowQuickReplies(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors border-b border-gray-100 last:border-0"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          className="w-10 h-10 mb-1 flex-shrink-0 rounded-full flex items-center justify-center transition-colors text-gray-500 hover:bg-gray-200 disabled:opacity-50"
          title="Attach File"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>
        <input 
          type="file" 
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
        
        <div className="flex-1 bg-gray-100/80 hover:bg-gray-100 transition-colors rounded-3xl flex items-end border border-transparent focus-within:border-gray-300 focus-within:bg-white shadow-inner px-4 py-1.5">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={disabled ? "Conversation resolved" : "Type a message..."}
            className="w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 resize-none max-h-32 min-h-[44px] py-2.5 text-gray-800 placeholder-gray-400 text-[15px]"
            rows="1"
            style={{ height: 'auto' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
            }}
          />
        </div>
        <button
          type="submit"
          disabled={disabled || (!content.trim() && !file)}
          className={`w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
            (content.trim() || file) && !disabled
              ? 'bg-green-500 hover:bg-green-600 text-white shadow-md active:scale-95'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
